import { Suspense, useDeferredValue, useState } from 'react'
import { createFileRoute, Link, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ButtonPrimitive } from '@/components/ui/button-primitive'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { LINK_CLASS, TraceLink } from './-components/admin/traces-shared'
import { LatencyHeatmap, Sparkline } from './-components/admin/metrics-charts'
import { formatCount, formatMs, formatPct, formatRate, tracesSinceFor } from './-components/admin/metrics-format'
import { EndpointsPending, HeatmapSkeleton, TableSkeleton } from './-components/admin/metrics-pending'
import {
  DeltaChip,
  EmptyPanel,
  KIND_LABELS,
  KindBadge,
  LatencyBar,
  MetricsSection,
  SEVERITY_TEXT,
  severity,
  SMALL_PLOT,
  TelemetryNotice,
} from './-components/admin/metrics-panels'

// Rate, errors and duration per tRPC procedure, API route and rendered page, ranked by where the server's time goes,
// under the distribution of every call's latency.

export const Route = createFileRoute('/_app/admin/metrics/endpoints')({
  component: RouteComponent,
  pendingComponent: EndpointsPending,
})

type Outputs = inferRouterOutputs<AppRouter>['admin']
type Endpoint = Outputs['metricsEndpoints']['endpoints'][number]

function useWindowContext() {
  return useMatch({ from: '/_app/admin/metrics/endpoints/_window' }).context
}

function RouteComponent() {
  // A view parameter of the heatmap's, not a query key: both distributions arrive in the one read.
  const [kind, setKind] = useState<'api' | 'ssr'>('api')
  return (
    <div className={SETTINGS_STACK}>
      <Suspense fallback={null}>
        <Notice />
      </Suspense>
      <MetricsSection
        copy="heatmap"
        actions={
          <Toggle
            label="Requests"
            value={kind}
            onChange={setKind}
            options={[
              ['api', 'API calls'],
              ['ssr', 'Page renders'],
            ]}
          />
        }
      >
        <Suspense fallback={<HeatmapSkeleton />}>
          <Heatmap kind={kind} />
        </Suspense>
      </MetricsSection>
      <MetricsSection copy="endpoints">
        <Suspense fallback={<TableSkeleton rows={10} toolbar />}>
          <EndpointsPanel />
        </Suspense>
      </MetricsSection>
    </div>
  )
}

function Notice() {
  const { data } = useSuspenseQuery(useWindowContext().metricsEndpointsQueryOptions)
  return <TelemetryNotice state={data.clickhouse} />
}

function Heatmap(props: { kind: 'api' | 'ssr' }) {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsHeatmapQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsHeatmapQueryOptions
  if (data.clickhouse !== 'ok') return <EmptyPanel>No latencies to plot.</EmptyPanel>
  return (
    <div className={cn('transition-opacity', stale && 'opacity-60')}>
      <LatencyHeatmap
        grid={{ bucketMs: data.bucketMs, spanMs: data.spanMs }}
        edgesMs={data.edgesMs}
        columns={props.kind === 'api' ? data.api : data.ssr}
        height={SMALL_PLOT}
      />
    </div>
  )
}

function Toggle<V extends string>(props: {
  label: string
  value: V
  onChange: (value: V) => void
  options: readonly (readonly [V, React.ReactNode])[]
}) {
  return (
    <div className="bg-muted inline-flex shrink-0 gap-1 rounded-md p-0.5" role="group" aria-label={props.label}>
      {props.options.map(([value, label]) => (
        <ButtonPrimitive
          key={value}
          type="button"
          aria-pressed={props.value === value}
          onClick={() => props.onChange(value)}
          className="text-muted-foreground aria-pressed:bg-card aria-pressed:text-foreground rounded px-2 py-0.5 text-xs font-medium aria-pressed:shadow-xs"
        >
          {label}
        </ButtonPrimitive>
      ))}
    </div>
  )
}

// MARK: The table

type KindFilter = 'all' | Endpoint['kind']
type SortKey = 'timeSharePct' | 'hot' | 'ratePerMin' | 'errorPct' | 'p50' | 'p95' | 'p99'

const SORT_COLUMNS: { key: SortKey; label: string; title?: string }[] = [
  { key: 'timeSharePct', label: 'Time', title: 'Share of all server time spent in the window: where the box goes' },
  { key: 'ratePerMin', label: 'Rate', title: 'Calls per minute, and the change in calls against the previous period' },
  { key: 'errorPct', label: 'Errors', title: 'Share of calls that failed on the server' },
  { key: 'p50', label: 'p50' },
  { key: 'p95', label: 'p95', title: 'p95, and its change against the previous period' },
  { key: 'p99', label: 'p99' },
  { key: 'hot', label: 'Hot', title: 'p95 + (p99 − p50): higher is slower or more inconsistent' },
]

// Enough to fill a laptop screen under the heatmap; the rest is one click away.
const PAGE_ROWS = 10

function EndpointsPanel() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsEndpointsQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsEndpointsQueryOptions
  const [kind, setKind] = useState<KindFilter>('all')
  const [filter, setFilter] = useState('')
  if (data.endpoints.length === 0)
    return (
      <EmptyPanel>{data.clickhouse === 'ok' ? 'Nothing was called in this window.' : 'No traffic to rank.'}</EmptyPanel>
    )
  const needle = filter.trim().toLowerCase()
  const rows = data.endpoints.filter(
    (row) => (kind === 'all' || row.kind === kind) && (!needle || row.endpoint.toLowerCase().includes(needle)),
  )
  const counts = { all: data.endpoints.length, trpc: 0, api: 0, ssr: 0 }
  for (const row of data.endpoints) counts[row.kind]++
  return (
    <div className={cn('flex flex-col gap-3 transition-opacity', stale && 'opacity-60')}>
      <div className="flex flex-wrap items-center gap-3">
        <Toggle
          label="Kind"
          value={kind}
          onChange={setKind}
          options={(['all', 'trpc', 'api', 'ssr'] as const).map((value) => [
            value,
            <>
              {value === 'all' ? 'All' : KIND_LABELS[value]}{' '}
              <span className="text-muted-foreground tabular-nums">{counts[value]}</span>
            </>,
          ])}
        />
        <Input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter endpoints"
          aria-label="Filter endpoints"
          className="h-8 w-56"
        />
      </div>
      <EndpointsTable rows={rows} spanMs={data.spanMs} />
    </div>
  )
}

function EndpointsTable(props: { rows: Endpoint[]; spanMs: number }) {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'timeSharePct', desc: true })
  const [all, setAll] = useState(false)
  // Ties break by call volume, so equal-latency rows still read busiest first.
  const sorted = [...props.rows].sort((a, b) => {
    const diff = a[sort.key] - b[sort.key] || a.calls - b.calls
    return sort.desc ? -diff : diff
  })
  const shown = all ? sorted : sorted.slice(0, PAGE_ROWS)
  const maxP99 = Math.max(1, ...props.rows.map((row) => row.p99))
  const since = tracesSinceFor(props.spanMs)
  return (
    <div className="flex flex-col gap-3">
      <div className="scrollbar w-full overflow-x-auto rounded-xl border">
        <Table className="min-w-[1040px] table-fixed border-collapse">
          <TableHeader className="bg-muted/50">
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[30%] px-3 py-2">Endpoint</TableHead>
              <TableHead className="text-muted-foreground w-28 px-3 py-2">Calls</TableHead>
              {SORT_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn('text-muted-foreground px-3 py-2 text-right', column.key === 'p99' && 'w-36')}
                  aria-sort={sort.key === column.key ? (sort.desc ? 'descending' : 'ascending') : undefined}
                >
                  <ButtonPrimitive
                    type="button"
                    title={column.title}
                    onClick={() =>
                      setSort((prev) => ({ key: column.key, desc: prev.key === column.key ? !prev.desc : true }))
                    }
                    className="hover:text-foreground inline-flex items-center gap-1"
                  >
                    {column.label}
                    <span aria-hidden className={sort.key === column.key ? '' : 'opacity-30'}>
                      {sort.key === column.key && !sort.desc ? '↑' : '↓'}
                    </span>
                  </ButtonPrimitive>
                </TableHead>
              ))}
              <TableHead className="text-muted-foreground w-24 px-3 py-2 text-right">Traces</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((row) => (
              <TableRow key={`${row.kind}:${row.endpoint}`}>
                <TableCell className="px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <KindBadge kind={row.kind} />
                    <span className="truncate font-mono text-xs" title={row.endpoint}>
                      {row.endpoint}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="px-3 py-2" title={`${formatCount(row.calls)} calls`}>
                  <Sparkline values={row.spark} />
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs font-medium tabular-nums">
                  {formatPct(row.timeSharePct)}
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs tabular-nums">
                  <div className="flex flex-col items-end">
                    <span>{formatRate(row.ratePerMin)}/min</span>
                    <DeltaChip deltaPct={row.callsDeltaPct} better="neutral" className="text-[10px]" />
                  </div>
                </TableCell>
                <TableCell
                  className={cn(
                    'px-3 py-2 text-right text-xs tabular-nums',
                    row.errors > 0 ? 'font-medium text-red-600 dark:text-red-400' : 'text-muted-foreground',
                  )}
                  title={row.errors > 0 ? `${formatCount(row.errors)} failed` : undefined}
                >
                  {row.errors > 0 ? formatPct(row.errorPct) : '—'}
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs tabular-nums">{formatMs(row.p50)}</TableCell>
                <TableCell className="px-3 py-2 text-right text-xs tabular-nums">
                  <div className="flex flex-col items-end">
                    <span>{formatMs(row.p95)}</span>
                    <DeltaChip deltaPct={row.p95DeltaPct} better="down" className="text-[10px]" />
                  </div>
                </TableCell>
                <TableCell className="px-3 py-2">
                  <LatencyBar ms={row.p99} max={maxP99} />
                </TableCell>
                <TableCell className="px-3 py-2 text-right">
                  <HotBadge hot={row.hot} />
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs">
                  <div className="flex flex-col items-end gap-0.5">
                    {row.slowest && (
                      <TraceLink traceId={row.slowest.traceId} at={row.slowest.at} className={LINK_CLASS}>
                        <span title={`The slowest call, ${formatMs(row.slowest.ms)}`}>slowest</span>
                      </TraceLink>
                    )}
                    <Link to="/admin/traces" search={{ q: row.endpoint, since }} className={LINK_CLASS}>
                      all
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {sorted.length > PAGE_ROWS && (
        <Button variant="outline" size="sm" className="self-start" onClick={() => setAll((prev) => !prev)}>
          {all ? `Show the top ${PAGE_ROWS}` : `Show all ${sorted.length}`}
        </Button>
      )}
    </div>
  )
}

function HotBadge(props: { hot: number }) {
  const level = severity(props.hot, 250, 1000)
  const background = { bad: 'bg-red-500/15', warn: 'bg-amber-500/15', good: 'bg-emerald-500/10' }[level]
  return (
    <span
      className={cn('rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums', background, SEVERITY_TEXT[level])}
    >
      {formatCount(props.hot)}
    </span>
  )
}
